// src/components/EnrichmentPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../constants';
import NewEnrichmentModal from './NewEnrichmentModal';
import NewCompanyEnrichmentModal from './NewCompanyEnrichmentModal';
import EnrichmentTypeSelector from './EnrichmentTypeSelector';
import './EnrichmentPage.css';

interface EnrichmentRecord {
  upload_id: string;
  enrichment_id?: string;
  enrichment_name: string;
  file_name: string;
  file_size: number;
  status: string;
  enrichment_status: string;
  created_at: string;
  updated_at: string;
  target_list_id?: string;
  total_rows?: number;
  successful_matches?: number;
  failed_matches?: number;
  stats?: {
    total_rows: number;
    successful_matches: number;
    failed_matches: number;
    match_rate?: number;
    processing_time_seconds?: number;
    linkedin_matches?: number;
    email_matches?: number;
    phone_matches?: number;
    uuid_count?: number;
  };
  uuid_list_url?: string;
  enriched_csv_download_url?: string;
  list_export_download_url?: string;
  estimated_cost?: number;
  person_uuids?: string[];
}

const EnrichmentPage: React.FC = () => {
  const { showToast, state } = useAppContext();
  const { apiKey } = useAuth();
  const { makeRequest } = useApi();
  
  const [enrichments, setEnrichments] = useState<EnrichmentRecord[]>([]);
  const [companyEnrichments, setCompanyEnrichments] = useState<EnrichmentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewEnrichmentModal, setShowNewEnrichmentModal] = useState(false);
  const [showNewCompanyEnrichmentModal, setShowNewCompanyEnrichmentModal] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [activeTab, setActiveTab] = useState<'contact' | 'company'>('contact');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [pageSize] = useState(10); // Show 10 enrichments per page

  // Load contact enrichments with pagination
  const loadEnrichments = useCallback(async (page: number = currentPage) => {
    if (!apiKey) return;
    
    setLoading(true);
    try {
      const response = await makeRequest(`${API_BASE_URL}/api/enrichment/source/csv/contacts/list?page=${page}&page_size=${pageSize}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (response?.data?.success) {
        setEnrichments(response.data.enrichments || []);
        setTotalPages(response.data.total_pages || 0);
        setTotalResults(response.data.total_results || 0);
        setCurrentPage(page);
      } else {
        throw new Error(response?.data?.error || 'Failed to load enrichments');
      }
    } catch (error) {
      console.error('Failed to load enrichments:', error);
      showToast({
        type: 'error',
        title: 'Error Loading Enrichments',
        message: 'Failed to load enrichment records'
      });
    } finally {
      setLoading(false);
    }
  }, [apiKey, makeRequest, showToast, currentPage, pageSize]);

  // Load company enrichments with pagination
  const loadCompanyEnrichments = useCallback(async (page: number = currentPage) => {
    if (!apiKey) return;
    
    setLoading(true);
    try {
      const response = await makeRequest(`${API_BASE_URL}/api/enrichment/source/csv/companies/list?page=${page}&page_size=${pageSize}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (response?.enrichments) {
        setCompanyEnrichments(response.enrichments || []);
        setTotalPages(response.total_pages || 0);
        setTotalResults(response.total || 0);
        setCurrentPage(page);
      } else if (response?.data?.enrichments) {
        // Handle wrapped response format
        setCompanyEnrichments(response.data.enrichments || []);
        setTotalPages(response.data.total_pages || 0);
        setTotalResults(response.data.total || 0);
        setCurrentPage(page);
      } else {
        // If no enrichments exist yet, set empty array
        setCompanyEnrichments([]);
        setTotalPages(0);
        setTotalResults(0);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to load company enrichments:', error);
      // Don't show error toast for empty results, just set empty state
      setCompanyEnrichments([]);
      setTotalPages(0);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [apiKey, makeRequest, currentPage, pageSize]);


  // Load enrichments on mount and when tab changes
  useEffect(() => {
    if (activeTab === 'contact') {
      loadEnrichments(1);
    } else {
      loadCompanyEnrichments(1);
    }
  }, [apiKey, activeTab]); // Reload when API key or tab changes

  // Monitor for completed enrichment export jobs (no auto-download)
  useEffect(() => {
    if (!state?.activeJobs) return;

    // Find completed enrichment export jobs
    const completedExportJobs = Array.from(state.activeJobs.values()).filter(
      job => job.job_type === 'enrichment_export' && job.status === 'completed' && job.result?.signed_download_url
    );

    if (completedExportJobs.length > 0) {
      // Just refresh the table to show the download button
      loadEnrichments(currentPage);
    }
  }, [state?.activeJobs, loadEnrichments, currentPage]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    if (activeTab === 'contact') {
      loadEnrichments(page);
    } else {
      loadCompanyEnrichments(page);
    }
  }, [loadEnrichments, loadCompanyEnrichments, activeTab]);

  // Handle new enrichment creation
  const handleSelectEnrichmentType = useCallback((type: 'contact' | 'company') => {
    setShowTypeSelector(false);
    if (type === 'contact') {
      setShowNewEnrichmentModal(true);
    } else {
      setShowNewCompanyEnrichmentModal(true);
    }
  }, []);

  const handleShowTypeSelector = useCallback(() => {
    setShowTypeSelector(true);
  }, []);


  // Handle enrichment completion
  const handleEnrichmentComplete = useCallback(async () => {
    setShowNewEnrichmentModal(false);
    
    // Small delay to ensure backend has updated the status
    setTimeout(() => {
      loadEnrichments(1); // Refresh the table to show the new enrichment, go to first page
    }, 1000);
  }, [loadEnrichments]);

  const handleCompanyEnrichmentComplete = useCallback(async () => {
    setShowNewCompanyEnrichmentModal(false);
    
    // Small delay to ensure backend has updated the status
    setTimeout(() => {
      loadCompanyEnrichments(1); // Refresh the table to show the new enrichment, go to first page
    }, 1000);
  }, [loadCompanyEnrichments]);

  // Handle export enrichment - just use the URL from the index
  const handleExportEnrichment = useCallback((enrichment: EnrichmentRecord) => {
    if (enrichment.enriched_csv_download_url) {
      window.open(enrichment.enriched_csv_download_url, '_blank');
    } else {
      showToast({
        type: 'error',
        title: 'Download Not Available',
        message: 'No download URL available for this enrichment'
      });
    }
  }, [showToast]);

  // Get status color and text
  const getStatusDisplay = (enrichmentStatus: string) => {
    switch (enrichmentStatus) {
      case 'completed':
        return { color: 'completed', text: 'Completed' };
      case 'started':
      case 'processing':
        return { color: 'running', text: 'Processing', description: 'Unlocking contacts...' };
      case 'failed':
        return { color: 'failed', text: 'Failed', description: 'Process failed' };
      case 'not_started':
      default:
        return { color: 'pending', text: 'Not Started' };
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format number with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Format percentage
  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  // Simple pagination component for enrichments
  const EnrichmentPagination = () => {
    if (totalPages <= 1) return null;

    const getPaginationPages = () => {
      const current = currentPage;
      const total = totalPages;
      const pageCountToShow = 5;

      if (total <= pageCountToShow) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      let start = Math.max(1, current - Math.floor(pageCountToShow / 2));
      let end = start + pageCountToShow - 1;

      if (end > total) {
        end = total;
        start = Math.max(1, end - pageCountToShow + 1);
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    const pages = getPaginationPages();

    return (
      <div className="enrichment-pagination">
        <div className="pagination-info">
          <span>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalResults).toLocaleString()} to{' '}
            {Math.min(currentPage * pageSize, totalResults).toLocaleString()} of{' '}
            {totalResults.toLocaleString()} enrichments
          </span>
        </div>
        
        <div className="pagination-controls">
          <button 
            className="pagination-button nav" 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            type="button"
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </button>
          
          {pages.map((page) => (
            <button
              key={page}
              type="button"
              className={`pagination-button ${currentPage === page ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button 
            className="pagination-button nav" 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            type="button"
          >
            <span>Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="enrichment-page">
      {/* Tab Navigation */}
      <div className="enrichment-tab-section">
        <div className="enrichment-tab-content">
          <div className="enrichment-tabs">
            <button
              className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contact Enrichment
            </button>
            <button
              className={`tab-button ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              Company Enrichment
            </button>
          </div>
          
          <div className="enrichment-actions">
            <button
              className="button-primary"
              onClick={handleShowTypeSelector}
            >
              New Enrichment
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="enrichment-content">
        {loading ? (
          <div className="loading-section">
            <div className="loading-spinner" />
            <p>Loading enrichments...</p>
          </div>
        ) : (activeTab === 'contact' ? enrichments : companyEnrichments).length === 0 ? (
          <div className="enrichment-empty">
            <FileText size={48} />
            <h3>No {activeTab === 'contact' ? 'Contact' : 'Company'} Enrichments Yet</h3>
            <p>Upload your first CSV file to start enriching {activeTab === 'contact' ? 'contact' : 'company'} data</p>
            <button
              className="button-primary"
              onClick={handleShowTypeSelector}
            >
              Create Your First Enrichment
            </button>
          </div>
        ) : (
          <>
            <div className="enrichment-table-container">
              <table className="enrichment-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Records</th>
                    <th>Matches</th>
                    <th>Match Rate</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeTab === 'contact' ? enrichments : companyEnrichments).map((enrichment) => {
                    const statusDisplay = getStatusDisplay(enrichment.enrichment_status);
                    
                    // Calculate match rate from stats or top-level data
                    const totalRows = enrichment.stats?.total_rows || enrichment.total_rows || 0;
                    const successfulMatches = enrichment.stats?.successful_matches || enrichment.successful_matches || 0;
                    const matchRate = totalRows && successfulMatches 
                      ? (successfulMatches / totalRows) * 100 
                      : 0;
                    
                    return (
                      <tr key={`${enrichment.upload_id}-${enrichment.created_at}`} className="enrichment-row">
                        <td className="enrichment-name">
                          <div className="enrichment-name-content">
                            <FileText size={16} />
                            <span>{enrichment.enrichment_name || enrichment.file_name}</span>
                          </div>
                        </td>
                        <td className="enrichment-status">
                          <div className={`status-badge status-${statusDisplay.color}`}>
                            <span>{statusDisplay.text}</span>
                          </div>
                          {statusDisplay.description && (
                            <div className="status-description">
                              {statusDisplay.description}
                            </div>
                          )}
                        </td>
                        <td className="enrichment-records">
                          {totalRows ? formatNumber(totalRows) : '-'}
                        </td>
                        <td className="enrichment-matches">
                          {successfulMatches !== undefined ? formatNumber(successfulMatches) : '-'}
                        </td>
                        <td className="enrichment-rate">
                          {matchRate > 0 ? formatPercentage(matchRate) : '-'}
                        </td>
                        <td className="enrichment-created">
                          {formatDate(enrichment.created_at)}
                        </td>
                        <td className="enrichment-actions">
                          {enrichment.enrichment_status === 'completed' && (enrichment.uuid_list_url || enrichment.enriched_csv_download_url || enrichment.list_export_download_url) ? (
                            <button
                              className="btn-icon success btn-sm"
                              onClick={() => handleExportEnrichment(enrichment)}
                              title="Download enriched contact data"
                            >
                              <Download size={14} />
                            </button>
                          ) : enrichment.enrichment_status === 'started' || enrichment.enrichment_status === 'processing' ? (
                            <button
                              className="btn-icon disabled btn-sm"
                              disabled
                              title="Export will be available when enrichment completes"
                            >
                              ⏳
                            </button>
                          ) : enrichment.enrichment_status === 'failed' ? (
                            <button
                              className="btn-icon error btn-sm"
                              disabled
                              title="Enrichment failed - cannot export"
                            >
                              ✕
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <EnrichmentPagination />
          </>
        )}
      </div>

      {/* New Enrichment Modal */}
      {showNewEnrichmentModal && (
        <NewEnrichmentModal
          isOpen={showNewEnrichmentModal}
          onClose={async () => {
            setShowNewEnrichmentModal(false);
            // Refresh the table to show updated status
            setTimeout(() => {
              loadEnrichments(1);
            }, 500);
          }}
          onComplete={handleEnrichmentComplete}
        />
      )}

      {/* New Company Enrichment Modal */}
      {showNewCompanyEnrichmentModal && (
        <NewCompanyEnrichmentModal
          isOpen={showNewCompanyEnrichmentModal}
          onClose={async () => {
            setShowNewCompanyEnrichmentModal(false);
            // Refresh the table to show updated status
            setTimeout(() => {
              loadCompanyEnrichments(1);
            }, 500);
          }}
          onComplete={handleCompanyEnrichmentComplete}
        />
      )}

      {/* Enrichment Type Selector Modal */}
      {showTypeSelector && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Choose Enrichment Type</h2>
              <button className="close-button" onClick={() => setShowTypeSelector(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <EnrichmentTypeSelector onSelectType={handleSelectEnrichmentType} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrichmentPage;
